import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Snackbar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

const Notification = ({
  open,
  message,
  severity = 'info',
  title = '',
  autoHideDuration = 5000,
  onClose,
  position = 'top-right'
}) => {
  const [isVisible, setIsVisible] = useState(open);

  useEffect(() => {
    setIsVisible(open);
  }, [open]);

  useEffect(() => {
    if (isVisible && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, autoHideDuration]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const getIcon = (severity) => {
    switch (severity) {
      case 'success':
        return <CheckCircleIcon fontSize="inherit" />;
      case 'error':
        return <ErrorIcon fontSize="inherit" />;
      case 'warning':
        return <WarningIcon fontSize="inherit" />;
      default:
        return <InfoIcon fontSize="inherit" />;
    }
  };

  const getPositionStyles = () => {
    const positions = {
      'top-right': {
        top: 20,
        right: 20,
        left: 'auto',
        bottom: 'auto'
      },
      'top-left': {
        top: 20,
        left: 20,
        right: 'auto',
        bottom: 'auto'
      },
      'bottom-right': {
        bottom: 20,
        right: 20,
        left: 'auto',
        top: 'auto'
      },
      'bottom-left': {
        bottom: 20,
        left: 20,
        right: 'auto',
        top: 'auto'
      },
      'top-center': {
        top: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        right: 'auto',
        bottom: 'auto'
      }
    };

    return positions[position] || positions['top-right'];
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 9999,
        minWidth: '300px',
        maxWidth: '500px',
        ...getPositionStyles(),
        animation: 'slideIn 0.3s ease-out'
      }}
    >
      <Alert
        severity={severity}
        icon={getIcon(severity)}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={handleClose}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
        sx={{
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid rgba(0,0,0,0.1)',
          '& .MuiAlert-message': {
            fontSize: '14px'
          }
        }}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {message}
      </Alert>

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Notification;